import { EvaluateResponse, InvestmentProfile, CompensationInfo } from './exchange-eval.model';

export type ReportType = 'sales' | 'exchange';
export type ProductRole = 'exchange' | 'recommend' | 'consider';
export type ProductType = 'VA' | 'FA' | 'IA' | 'RILA';
export type Gender = 'M' | 'F';
export type WizardStep = 1 | 2 | 3 | 4;

export interface DisclosureState {
  reportType: ReportType;
  clientName: string;
  advisorName: string;
  state: string;
  products: DisclosureProduct[];
  step: WizardStep;
  exchangeEvaluation: EvaluateResponse | null;
  lastExchangeDate: string | null;
  customerAge: number | null;
  customerGender: Gender | null;
  contractValue: number | null;
  benefitBase: number | null;
  purchasePayment: number | null;
  investmentProfile: InvestmentProfile;
  producerRationale: string | null;
  compensation: CompensationInfo;
}

export interface SelectedRider {
  id: number;
  name: string;
  type: string;
}

export interface DisclosureProduct {
  /** Stable identity for this row, independent of array position — lets the
   *  Product Selection step's @for track a row by what it *is* rather than
   *  where it sits, so removing one row can never hand a neighboring row's
   *  stateful <app-product-card> (its own catalog fetch, currentType,
   *  companyFilter, etc.) to a different product by reusing its slot.
   *  Optional: only rows added through DisclosureStateService.addProduct()
   *  are guaranteed to have one; other constructors of this type (BLANK_PRODUCT,
   *  preload/test fixtures for the report page) don't need it. */
  uid?: string;
  role: ProductRole;
  productType: ProductType;
  productId: number | null;
  contractDate: string | null;
  selectedRiderIds: number[];
  selectedRiders: SelectedRider[];
  productData: any | null;
}

export const BLANK_INVESTMENT_PROFILE: InvestmentProfile = {
  riskTolerance: null,
  liquidityNeeds: null,
  timeHorizon: null,
  investmentObjective: null,
  annualIncome: null,
  netWorth: null,
  liquidNetWorth: null,
  taxBracket: null,
  existingAnnuityCount: null,
  existingAnnuityValue: null,
  sourceOfFunds: null,
  investmentExperience: null,
  essentialExpenses: null,
  otherRelevantInfo: null,
};

export const BLANK_COMPENSATION: CompensationInfo = {
  existingTrailCommission: null,
  recommendedUpfrontCommission: null,
  recommendedTrailCommission: null,
  higherCompensationOnRecommended: false,
  conflictDisclosure: null,
};

export const BLANK_STATE: DisclosureState = {
  reportType: 'exchange',
  clientName: '',
  advisorName: '',
  state: '',
  products: [],
  step: 1,
  exchangeEvaluation: null,
  lastExchangeDate: null,
  customerAge: null,
  customerGender: null,
  contractValue: null,
  benefitBase: null,
  purchasePayment: null,
  investmentProfile: { ...BLANK_INVESTMENT_PROFILE },
  producerRationale: null,
  compensation: { ...BLANK_COMPENSATION },
};

export const BLANK_PRODUCT: DisclosureProduct = {
  role: 'recommend',
  productType: 'VA',
  productId: null,
  contractDate: null,
  selectedRiderIds: [],
  selectedRiders: [],
  productData: null,
};

export type { EvaluateResponse } from './exchange-eval.model';
